/**
 * RCAWizard — main wizard orchestrator page.
 *
 * Steps: welcome → phases → assessment → (navigate to /assess/:id for results)
 *
 * URL pattern:
 *   /assess         — new assessment
 *   /assess?id=xyz  — resume existing assessment (project-scoped)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { WizardProgress } from '@/components/wizard/WizardProgress';
import { WizardScreen } from '@/components/wizard/WizardScreen';
import { useRCA } from '@/context/RCAContext';
import { PHASES, PHASE_NAMES } from '@/data/rcaQuestions';
import { useSaveAssessment } from '@/hooks/useRCAData';
import { cn } from '@/lib/utils';
import type { PhaseId, AnswerValue } from '@/types/rca';

// ============================================================
// Constants
// ============================================================

const PHASE_DESCRIPTIONS: Record<PhaseId, string> = {
  pre_charter: 'Reviews priorities, business need, and factors impacting project success. Performed at the beginning of the Initiation Phase.',
  initiation: 'Confirms or adjusts risk & complexity level following initial project documentation.',
  planning: 'Reviews planning documents and previous assessments. Establishes controls for Execution.',
  event_driven: 'Triggered by a significant change (cost, schedule, or scope) from the project baseline.',
};

const AUTO_SAVE_INTERVAL_MS = 30_000;

// ============================================================
// Helper: build answer key
// ============================================================

function answerKey(phaseId: PhaseId, section: 'risk' | 'complexity', questionNum: string): string {
  return `${phaseId}_${section}_${questionNum}`;
}

// ============================================================
// Sub-components
// ============================================================

function AutoSaveIndicator({ savedAt }: { savedAt: string | null }) {
  if (!savedAt) return null;

  const relTime = (() => {
    const diff = Date.now() - new Date(savedAt).getTime();
    const secs = Math.floor(diff / 1000);
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    return `${mins}m ago`;
  })();

  return (
    <div className="fixed bottom-4 right-4 flex items-center gap-1.5 text-xs text-muted-foreground bg-card border border-border rounded-full px-3 py-1.5 shadow-soft">
      <span className="w-1.5 h-1.5 rounded-full bg-risk-low animate-pulse" />
      Saved {relTime}
    </div>
  );
}

// ============================================================
// Welcome Screen
// ============================================================

function WelcomeScreen({
  onStart,
  onResume,
  draftProject,
}: {
  onStart: () => void;
  onResume: () => void;
  draftProject: string | null;
}) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background">
      <div className="w-full max-w-lg space-y-8 text-center wizard-screen-enter">
        {/* Logo */}
        <div className="flex justify-center">
          <img
            src="/logo-primary.svg"
            alt="RCA Tool"
            className="h-16 object-contain"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>

        {/* Heading */}
        <div className="space-y-3">
          <h1 className="text-3xl font-bold text-foreground">
            Project Risk &amp; Complexity Assessment
          </h1>
          <p className="text-muted-foreground text-sm leading-relaxed max-w-md mx-auto">
            Answer questions across four phases — Pre-Charter, Initiation, Planning, and Event-Driven — to score your project's risk and complexity and determine the required governance controls.
          </p>
        </div>

        {/* Resume banner */}
        {draftProject && (
          <div className="rounded-lg border border-cot-blue/30 bg-cot-blue/5 p-4 flex items-center justify-between gap-3 text-left">
            <div>
              <p className="text-sm font-medium text-cot-blue">Resume draft assessment</p>
              <p className="text-xs text-muted-foreground truncate max-w-xs">{draftProject}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={onStart}
                className="text-xs border-cot-blue/30 text-muted-foreground"
              >
                Start new
              </Button>
              <Button
                size="sm"
                onClick={onResume}
                className="text-xs bg-cot-blue hover:bg-cot-blue/90 text-white"
              >
                Resume
              </Button>
            </div>
          </div>
        )}

        {/* Begin button */}
        {!draftProject && (
          <Button
            size="lg"
            onClick={onStart}
            className="bg-cot-blue hover:bg-cot-blue/90 text-white px-10"
          >
            Begin Assessment
          </Button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Phase Selection Screen
// ============================================================

function PhaseSelectionScreen({
  selectedPhases,
  onToggle,
  onStart,
}: {
  selectedPhases: PhaseId[];
  onToggle: (id: PhaseId) => void;
  onStart: () => void;
}) {
  const canStart = selectedPhases.length >= 1;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background">
      <div className="w-full max-w-2xl space-y-6 wizard-screen-enter">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-semibold text-foreground">Select Assessment Phases</h2>
          <p className="text-sm text-muted-foreground">
            Choose which phases apply to this project. At least one phase is required.
          </p>
        </div>

        <div className="grid gap-3">
          {PHASES.map((phase) => {
            const isSelected = selectedPhases.includes(phase.id);
            const isRequired = phase.id === 'pre_charter';

            return (
              <Card
                key={phase.id}
                className={cn(
                  'cursor-pointer transition-all duration-150 hover:shadow-soft border-2',
                  isSelected
                    ? 'border-cot-blue bg-cot-blue/5'
                    : 'border-border hover:border-cot-blue/30',
                )}
                onClick={() => !isRequired && onToggle(phase.id)}
              >
                <CardContent className="flex items-start gap-4 p-4">
                  <Checkbox
                    checked={isSelected}
                    disabled={isRequired}
                    onCheckedChange={() => !isRequired && onToggle(phase.id)}
                    className="mt-0.5 border-cot-blue data-[state=checked]:bg-cot-blue data-[state=checked]:border-cot-blue"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm text-foreground">{PHASE_NAMES[phase.id]}</p>
                      {isRequired && (
                        <Badge variant="secondary" className="text-xs">Required</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {PHASE_DESCRIPTIONS[phase.id]}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={onStart}
            disabled={!canStart}
            className="bg-cot-blue hover:bg-cot-blue/90 text-white px-8"
          >
            Start Assessment
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Main Wizard Page
// ============================================================

export default function RCAWizard() {
  const { state, dispatch } = useRCA();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const saveAssessmentMutation = useSaveAssessment();
  const autoSaveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Local wizard step (not stored in context — we use context step for persistence)
  // We mirror context step but override on mount if URL has ?id=
  const wizardStep = state.step === 'welcome' || state.step === 'phases' || state.step === 'assessment'
    ? state.step
    : 'welcome';

  // Check if we have a draft in localStorage
  const hasDraft =
    state.project !== '' &&
    Object.keys(state.answers).length > 0 &&
    state.step !== 'welcome';

  // Phase selection state mirrors context activePhases
  const [selectedPhases, setSelectedPhases] = useState<PhaseId[]>(
    state.activePhases.length > 0
      ? state.activePhases
      : ['pre_charter', 'initiation', 'planning', 'event_driven'],
  );

  // On mount: if URL has ?id, we'd load that project — for now just sync selected phases
  useEffect(() => {
    const projectId = searchParams.get('id');
    if (projectId) {
      // If routed here with a project id but no assessment state, start fresh
      dispatch({ type: 'RESET' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save every 30 seconds if in assessment step and has projectId
  useEffect(() => {
    if (wizardStep !== 'assessment') return;
    autoSaveTimerRef.current = setInterval(() => {
      if (state.assessmentId) {
        const toSave = {
          assessment_id: state.assessmentId,
          assessor: state.assessor,
          assessor_email: state.assessorEmail,
          status: state.status,
          active_phases: state.activePhases,
          answers: state.answers,
        };
        saveAssessmentMutation.mutate(toSave);
      }
    }, AUTO_SAVE_INTERVAL_MS);

    return () => {
      if (autoSaveTimerRef.current) clearInterval(autoSaveTimerRef.current);
    };
  }, [wizardStep, state.assessmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------
  // Navigation handlers
  // -------------------------------------------------------

  function handleStartNew() {
    dispatch({ type: 'RESET' });
    dispatch({ type: 'SET_STEP', step: 'phases' });
  }

  function handleResume() {
    dispatch({ type: 'SET_STEP', step: 'assessment' });
  }

  function handleTogglePhase(id: PhaseId) {
    setSelectedPhases((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  }

  function handleStartAssessment() {
    // Sort phases in canonical order
    const order: PhaseId[] = ['pre_charter', 'initiation', 'planning', 'event_driven'];
    const sorted = order.filter((p) => selectedPhases.includes(p));
    dispatch({ type: 'SET_PROJECT_META', payload: { activePhases: sorted } });
    dispatch({ type: 'SET_PHASE', phaseIdx: 0 });
    dispatch({ type: 'SET_SECTION', section: 'risk' });
    dispatch({ type: 'SET_QUESTION', questionIdx: 0 });
    dispatch({ type: 'SET_STEP', step: 'assessment' });
  }

  // -------------------------------------------------------
  // Current question lookup
  // -------------------------------------------------------

  const activePhases = state.activePhases;
  const currentPhaseId = activePhases[state.currentPhaseIdx];
  const currentPhase = PHASES.find((p) => p.id === currentPhaseId);
  const currentSectionData = currentPhase
    ? state.currentSection === 'risk'
      ? currentPhase.risk
      : currentPhase.complexity
    : null;
  const currentQuestion = currentSectionData?.questions[state.currentQuestionIdx] ?? null;

  // Build current answer key
  const currentAnswerKey = currentQuestion && currentPhaseId
    ? answerKey(currentPhaseId, state.currentSection, currentQuestion.num)
    : null;
  const currentAnswer = currentAnswerKey ? (state.answers[currentAnswerKey] ?? null) : null;

  // Is this the last question in the entire wizard?
  const isLastPhase = state.currentPhaseIdx === activePhases.length - 1;
  const currentPhaseObj = PHASES.find((p) => p.id === currentPhaseId);
  const hasComplexity = currentPhaseObj?.complexity !== null;
  const isLastSection =
    state.currentSection === 'complexity' ||
    (!hasComplexity && state.currentSection === 'risk');
  const lastQuestionsInSection = currentSectionData?.questions.length ?? 0;
  const isLastQuestion =
    isLastPhase &&
    isLastSection &&
    state.currentQuestionIdx === lastQuestionsInSection - 1;

  // -------------------------------------------------------
  // Answer handler
  // -------------------------------------------------------

  const handleAnswer = useCallback(
    (value: AnswerValue) => {
      if (!currentAnswerKey) return;
      dispatch({ type: 'SET_ANSWER', key: currentAnswerKey, value });
    },
    [currentAnswerKey, dispatch],
  );

  // -------------------------------------------------------
  // Next / Prev handlers
  // -------------------------------------------------------

  function handleNext() {
    if (!currentSectionData || !currentPhase) return;

    const questionsInSection = currentSectionData.questions.length;
    const hasMoreQuestionsInSection = state.currentQuestionIdx < questionsInSection - 1;

    if (hasMoreQuestionsInSection) {
      dispatch({ type: 'SET_QUESTION', questionIdx: state.currentQuestionIdx + 1 });
      return;
    }

    // End of current section — move to complexity or next phase
    if (state.currentSection === 'risk' && currentPhase.complexity !== null) {
      dispatch({ type: 'SET_SECTION', section: 'complexity' });
      dispatch({ type: 'SET_QUESTION', questionIdx: 0 });
      return;
    }

    // End of phase — move to next phase or finish
    const nextPhaseIdx = state.currentPhaseIdx + 1;
    if (nextPhaseIdx < activePhases.length) {
      dispatch({ type: 'SET_PHASE', phaseIdx: nextPhaseIdx });
      dispatch({ type: 'SET_SECTION', section: 'risk' });
      dispatch({ type: 'SET_QUESTION', questionIdx: 0 });
      return;
    }

    // All phases done — navigate to results
    dispatch({ type: 'SET_STEP', step: 'results' });
    navigate('/assess/' + state.assessmentId);
  }

  function handlePrev() {
    if (state.currentQuestionIdx > 0) {
      dispatch({ type: 'SET_QUESTION', questionIdx: state.currentQuestionIdx - 1 });
      return;
    }

    // First question of section — go back to previous section or phase
    if (state.currentSection === 'complexity') {
      dispatch({ type: 'SET_SECTION', section: 'risk' });
      const prevPhase = PHASES.find((p) => p.id === currentPhaseId);
      const prevCount = prevPhase?.risk.questions.length ?? 1;
      dispatch({ type: 'SET_QUESTION', questionIdx: prevCount - 1 });
      return;
    }

    // First question of risk section — go back to previous phase
    if (state.currentPhaseIdx > 0) {
      const prevPhaseIdx = state.currentPhaseIdx - 1;
      const prevPhaseId = activePhases[prevPhaseIdx];
      const prevPhaseObj = PHASES.find((p) => p.id === prevPhaseId);
      const prevSection = prevPhaseObj?.complexity !== null ? 'complexity' : 'risk';
      const prevSectionData =
        prevSection === 'complexity' ? prevPhaseObj?.complexity : prevPhaseObj?.risk;
      const prevCount = prevSectionData?.questions.length ?? 1;

      dispatch({ type: 'SET_PHASE', phaseIdx: prevPhaseIdx });
      dispatch({ type: 'SET_SECTION', section: prevSection });
      dispatch({ type: 'SET_QUESTION', questionIdx: prevCount - 1 });
    }
  }

  const canGoPrev =
    state.currentQuestionIdx > 0 ||
    state.currentSection === 'complexity' ||
    state.currentPhaseIdx > 0;

  // -------------------------------------------------------
  // Render
  // -------------------------------------------------------

  if (wizardStep === 'welcome') {
    return (
      <WelcomeScreen
        onStart={handleStartNew}
        onResume={handleResume}
        draftProject={hasDraft ? state.project || 'Untitled Project' : null}
      />
    );
  }

  if (wizardStep === 'phases') {
    return (
      <PhaseSelectionScreen
        selectedPhases={selectedPhases}
        onToggle={handleTogglePhase}
        onStart={handleStartAssessment}
      />
    );
  }

  // Assessment step
  if (!currentQuestion || !currentPhaseId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <CheckCircle2 className="w-12 h-12 text-risk-low mx-auto" />
          <p className="text-lg font-medium text-foreground">Assessment complete.</p>
          <Button
            onClick={() => navigate('/assess/' + state.assessmentId)}
            className="bg-cot-blue hover:bg-cot-blue/90 text-white"
          >
            View Results
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 pt-8 pb-24 space-y-8">
        {/* Progress */}
        <WizardProgress
          activePhases={activePhases}
          currentPhaseIdx={state.currentPhaseIdx}
          currentSection={state.currentSection}
          currentQuestionIdx={state.currentQuestionIdx}
          totalQuestionsInSection={currentSectionData?.questions.length ?? 0}
        />

        {/* Question screen — key forces re-mount on question change for animation */}
        <div key={`${currentPhaseId}_${state.currentSection}_${currentQuestion.num}`}>
          <WizardScreen
            question={currentQuestion}
            phaseId={currentPhaseId}
            selected={currentAnswer}
            onAnswer={handleAnswer}
            onNext={handleNext}
            onPrev={handlePrev}
            canGoNext={currentAnswer !== null}
            canGoPrev={canGoPrev}
            isLastQuestion={isLastQuestion}
          />
        </div>
      </div>

      {/* Auto-save indicator */}
      <AutoSaveIndicator savedAt={state.savedAt} />
    </div>
  );
}
